import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Activity, CircleCheckBig, Clock4, Download, Filter, LayoutGrid, RadioTower, RefreshCw, Settings, TriangleAlert } from "lucide-react";
import AppShell from "../shell/AppShell.jsx";
import DetailDrawer from "../components/DetailDrawer.jsx";
import WorkerRecordDrawer from "../workerRecord/WorkerRecordDrawer.jsx";
import { useBackofficeIdentity } from "../hierarchy/BackofficeIdentityContext.jsx";
import { useAbnormalEventsList, ABNORMAL_EVENTS_DEFAULT_PAGE_SIZE } from "./useAbnormalEventsList.js";
import { getAbnormalEventDetail, runHeartbeatScan, acknowledgeAbnormalEvent, resolveAbnormalEvent } from "./abnormalApi.js";
import { useAbnormalSummaryCounts } from "./useAbnormalSummaryCounts.js";
import { downloadCsv, fetchAllAbnormalEvents, toCsv, MAX_ROWS } from "./exportAbnormalEvents.js";
import "./abnormalHeartbeat.css";
import "../pages/states.css";
import Pagination from "../components/Pagination.jsx";
import RecordDrawerHeader from "../components/RecordDrawerHeader.jsx";

import PageSkeleton, { DetailSkeleton } from "../components/Skeleton.jsx";
const CRUMBS = ["Backoffice", "Abnormal", "Missing Heartbeat"];
const DRAWER_TITLE_ID = "abn-drawer-title";
const SEARCH_DEBOUNCE_MS = 350;

// Prompt 142B: "MissingHeartbeat" was the only value this page's own Type
// filter ever exposed. The real backend AbnormalEventTypes class
// (Back/WorkmateX/API/RootAPI/Models/Backoffice/AbnormalDtos.cs) defines
// three more real values that already write into this exact same
// dbo.WmxAbnormalEvents.AbnormalType column, from their own real services
// (GeofenceZoneService "OutOfZone", FakeLocationService
// "FakeLocationSuspected", FaceCheckService "FaceCheckMissing") - closing a
// genuine frontend gap here, never inventing a new type. Every other real
// page (Geofence Zones, Fake Location Review, Face Check Daily) already
// shows its own type verbatim as a badge (e.g. "OutOfZone"), so these
// option labels match that same established convention instead of
// inventing friendlier phrasing.
const ABNORMAL_TYPE_OPTIONS = ["MissingHeartbeat", "OutOfZone", "FakeLocationSuspected", "FaceCheckMissing"];
const SEVERITY_OPTIONS = ["Low", "Medium", "High"];
const STATUS_OPTIONS = ["Open", "Acknowledged", "Resolved"];

// Prompt 142: page purpose must be understandable above the fold without
// scanning anything - title/subtitle/helper line make clear this is a
// monitor for events the backend has already detected, never a "start
// tracking"/"enable GPS" control (this page never calls a tracking-start or
// device-provisioning API).
const PAGE_TITLE = "Missing Heartbeat Monitor";
const SUBTITLE_TEXT = "ติดตามแรงงานที่เคยเริ่มส่ง Heartbeat แล้วหยุดส่งเกิน threshold";
const HELPER_TEXT = "หน้านี้ใช้ติดตามเหตุผิดปกติที่ระบบตรวจพบแล้ว ไม่ได้เริ่ม tracking และไม่ได้เปิด GPS ให้แรงงาน";

// Prompt 142B: the old page had a full-width amber policy banner up top -
// replaced with the same small dotted-underline "เกี่ยวกับข้อมูลนี้"-plus-
// native-title-tooltip pattern War Room's own footer already established,
// so the same real policy facts are still one hover away without occupying
// header space.
const POLICY_TOOLTIP =
  "ตรวจจากสถานะล่าสุดของแรงงาน ไม่ได้ไล่ดูประวัติทั้งหมด · เตรียมรายชื่อผู้ที่ต้องแจ้งไว้ให้ คือ Agent และเพื่อนประกันสูงสุด 5 คน · ระบบยังไม่แจ้งให้อัตโนมัติ ต้องติดต่อเอง";


// Prompt 142: the diagnostic scan is a SuperAdmin-only admin tool, secondary
// to the main triage workflow - never surfaced as the page's primary CTA,
// never rendered at all for a non-SuperAdmin actor.
const SCAN_WARNING_LINES = ["ใช้สำหรับ SuperAdmin ตรวจสอบระบบเท่านั้น", "dryRun จะไม่สร้างเหตุผิดปกติ"];

// Prompt 142: the raw runHeartbeatScan() response field names
// (scannedCount/candidateCount/...) are real, unrenamed API contract keys -
// this map only controls how each one is *labeled* in the UI, never what's
// requested/returned. Order here is also the render order.
const SCAN_RESULT_FIELD_LABELS = [
  ["scannedCount", "ตรวจแรงงานที่เคยมี Heartbeat"],
  ["candidateCount", "เข้าเงื่อนไข Heartbeat หาย"],
  ["createdCount", "สร้างเหตุผิดปกติใหม่"],
  ["skippedOpenDuplicateCount", "ข้ามเพราะมีเคส Open อยู่แล้ว"],
  ["recipientPreparedCount", "เตรียมรายชื่อผู้เกี่ยวข้อง"],
];

const EMPTY_NO_EVENTS_TITLE = "ยังไม่มีเหตุ Missing Heartbeat ในขอบเขตนี้";
const EMPTY_NO_EVENTS_SUB = "ถ้าแรงงานยังไม่เคยเริ่มส่ง Heartbeat ระบบจะยังไม่ถือว่า Missing Heartbeat";
const EMPTY_FILTERED_TEXT = "ไม่พบเหตุผิดปกติตามตัวกรองนี้";

const HEARTBEAT_PREREQUISITE_TITLE = "ต้องมีข้อมูล Heartbeat จากมือถือก่อน";
const HEARTBEAT_PREREQUISITE_STEPS = [
  "1. แรงงานผูกเครื่องและเริ่มใช้งาน mobile app",
  "2. มือถือส่ง Heartbeat เข้าระบบ",
  "3. ถ้า Heartbeat หายเกิน threshold ระบบจะแสดงเหตุในหน้านี้",
];

const ACKNOWLEDGE_NOTICE_LINES = [
  "ยังไม่ resolve — สถานะจะเปลี่ยนเป็น Acknowledged เท่านั้น การปิดเหตุต้องกด Resolve แยกต่างหาก",
  "ทุกการ acknowledge จะถูกบันทึก audit",
];

const RESOLVE_WARNING_TEXT = "การ resolve ต้องมีเหตุผลและถูกบันทึก audit";

const SEVERITY_BADGE_VARIANT = { Low: "info", Medium: "warning", High: "danger" };
const STATUS_BADGE_VARIANT = { Open: "warning", Acknowledged: "info", Resolved: "success" };

function severityBadgeVariant(value) {
  return SEVERITY_BADGE_VARIANT[value] ?? "neutral";
}

function statusBadgeVariant(value) {
  return STATUS_BADGE_VARIANT[value] ?? "neutral";
}

// Prompt 142B Part 4: every tab is a real, honest preset built only from
// real fields (status/abnormalType) - never an invented status. "Other
// Abnormal" is the one deliberate exception worth documenting here: the
// backend's abnormalType filter is a single exact-match column check
// (AbnormalHeartbeatRepository's own filter clause: `ae.AbnormalType =
// @filterAbnormalType`, no not-equal/IN support), so "every type except
// MissingHeartbeat" can't be expressed server-side in one request. That tab
// instead fetches unfiltered-by-type (the same request "ทั้งหมด" makes) and
// excludes MissingHeartbeat client-side from the current page only - see
// this file's own isOtherAbnormalActive handling below, which labels the
// resulting count honestly as page-scoped rather than presenting it as a
// real platform-wide total (the same "(หน้านี้)" convention this codebase
// already uses whenever no real server-side aggregate exists for something).
/**
 * P166: the six summary tiles. Order matches the tab strip below them on
 * purpose - the strip is how you act on a number, so a tile that sits in a
 * different place from its tab makes you hunt for the way in.
 */
const SUMMARY_TILES = [
  { key: "total", label: "ทั้งหมด", tone: "primary", Icon: Activity },
  { key: "open", label: "Open", tone: "danger", Icon: RadioTower },
  { key: "acknowledged", label: "Acknowledged", tone: "warning", Icon: Clock4 },
  { key: "resolved", label: "Resolved", tone: "success", Icon: CircleCheckBig },
  { key: "missingHeartbeat", label: "Missing Heartbeat", tone: "alert", Icon: TriangleAlert },
  { key: "otherAbnormal", label: "Other Abnormal", tone: "muted", Icon: LayoutGrid },
];

const STATUS_TABS = [
  { key: "all", label: "ทั้งหมด" },
  { key: "open", label: "Open", status: "Open" },
  { key: "acknowledged", label: "Acknowledged", status: "Acknowledged" },
  { key: "resolved", label: "Resolved", status: "Resolved" },
  { key: "missingHeartbeat", label: "Missing Heartbeat", abnormalType: "MissingHeartbeat" },
  { key: "otherAbnormal", label: "Other Abnormal", excludeMissingHeartbeat: true },
];

function formatDateTimeTh(value) {
  if (!value) {
    return null;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  const datePart = date.toLocaleDateString("th-TH-u-ca-gregory", { day: "2-digit", month: "short", year: "numeric" });
  const timePart = date.toLocaleTimeString("th-TH", { hour: "2-digit", minute: "2-digit", hour12: false });
  return `${datePart} · ${timePart} ICT`;
}

function describeListError(error) {
  if (error?.status === 401) {
    return "เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่";
  }
  if (error?.status === 403) {
    return "คุณไม่มีสิทธิ์เข้าถึงรายการเหตุผิดปกตินี้";
  }
  return "กรุณาลองรีเฟรชหน้านี้อีกครั้ง หากยังพบปัญหากรุณาติดต่อฝ่ายปฏิบัติการ";
}

function describeDetailError(error) {
  if (error?.status === 401) {
    return "เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่";
  }
  if (error?.status === 403) {
    return "คุณไม่มีสิทธิ์ดูรายละเอียดเหตุผิดปกตินี้";
  }
  if (error?.status === 404) {
    return "ไม่พบเหตุผิดปกตินี้ อาจถูกลบหรือย้ายแล้ว";
  }
  return "ไม่สามารถโหลดรายละเอียดได้ กรุณาลองใหม่อีกครั้ง";
}

function describeScanError(error) {
  if (error?.status === 403) {
    return "เฉพาะ SuperAdmin เท่านั้นที่สามารถเรียกใช้ Manual Scan ได้";
  }
  if (error?.status === 401) {
    return "เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่";
  }
  return "ไม่สามารถเรียกใช้ Manual Scan ได้ กรุณาลองใหม่อีกครั้ง";
}

function describeWriteError(error, conflictFallback) {
  if (error?.status === 403) {
    return "คุณไม่มีสิทธิ์ทำรายการนี้ เฉพาะ SuperAdmin หรือ Agent เจ้าของงานเท่านั้น";
  }
  if (error?.status === 404) {
    return "ไม่พบเหตุผิดปกตินี้ อาจถูกลบหรือย้ายแล้ว";
  }
  if (error?.status === 409) {
    return conflictFallback;
  }
  if (error?.status === 400) {
    return "กรุณาตรวจสอบข้อมูลที่กรอกอีกครั้ง";
  }
  return "ไม่สามารถบันทึกรายการนี้ได้ กรุณาลองใหม่อีกครั้ง";
}

/**
 * /backoffice/abnormal/heartbeat - the Backoffice Abnormal Heartbeat screen
 * (Prompt 117B, redesigned as a triage screen by Prompt 142B). Entirely over
 * the real Prompt 116 Backoffice Abnormal Heartbeat API: a paginated,
 * scope-isolated list of abnormal events (GET .../abnormal/events), a
 * single-event detail with prepared recipients (GET
 * .../abnormal/events/{id}), a SuperAdmin-only manual scan (POST
 * .../abnormal/heartbeat-scan), and acknowledge/resolve (POST
 * .../abnormal/events/{id}/acknowledge|resolve). This screen never calls a
 * geofence/fake-location/face-recognition/SOS API (none of those exist
 * anywhere in this codebase), never scans event tables (the backend's own
 * scan reads dbo.WmxWorkerLatestStatus only), and never sends a real
 * notification - notificationDeliveryStarted is always rendered false with
 * the backend's own fixed reserved-reason string. There is no summary/KPI
 * endpoint in P116 - Prompt 142B removed the old "รอ Summary API" reserved
 * KPI tiles entirely rather than fake an aggregate; the status tabs below
 * are the real workflow surface instead, and the list's own "N รายการ" head
 * text is always the real server totalCount for whatever's currently
 * filtered (except the one honestly page-scoped exception documented on
 * STATUS_TABS above).
 */
export default function AbnormalHeartbeatPage() {
  const { data: identity } = useBackofficeIdentity();
  const primaryRole = identity?.primaryRole;

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [abnormalTypeFilter, setAbnormalTypeFilter] = useState("");
  const [severityFilter, setSeverityFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [excludeMissingHeartbeat, setExcludeMissingHeartbeat] = useState(false);
  const [superAgentOrgInput, setSuperAgentOrgInput] = useState("");
  const [plcOrgInput, setPlcOrgInput] = useState("");
  const [agentIdInput, setAgentIdInput] = useState("");
  const [employerIdInput, setEmployerIdInput] = useState("");
  const [workerIdInput, setWorkerIdInput] = useState("");
  const [page, setPage] = useState(1);
  // Clicking the row opens the ONE shared worker record; the row's own
  // action button keeps opening this screen's process detail.
  const [workerRecordId, setWorkerRecordId] = useState(null);
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  const [exportState, setExportState] = useState({ status: "idle", message: null });
  // Refresh has to move the tiles too, or they keep reporting the state the
  // list has just moved past - which is the whole reason Refresh was pressed.
  const [summaryReloadToken, setSummaryReloadToken] = useState(0);
  const filterAnchorRef = useRef(null);

  const [selectedEventId, setSelectedEventId] = useState(null);
  const [drawerMode, setDrawerMode] = useState(null); // null | "detail" | "acknowledge" | "resolve" | "scan"
  const [detailState, setDetailState] = useState({ status: "idle", data: null, error: null, forId: null });
  const [detailReloadToken, setDetailReloadToken] = useState(0);

  const [noteInput, setNoteInput] = useState("");
  const [acknowledgeState, setAcknowledgeState] = useState({ status: "idle", error: null });

  const [resolutionNoteInput, setResolutionNoteInput] = useState("");
  const [resolveValidationError, setResolveValidationError] = useState(null);
  const [resolveState, setResolveState] = useState({ status: "idle", error: null });

  const [scanNowUtcInput, setScanNowUtcInput] = useState("");
  const [scanDryRun, setScanDryRun] = useState(true);
  const [scanState, setScanState] = useState({ status: "idle", error: null, result: null });

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [
    search,
    abnormalTypeFilter,
    severityFilter,
    statusFilter,
    excludeMissingHeartbeat,
    superAgentOrgInput,
    plcOrgInput,
    agentIdInput,
    employerIdInput,
    workerIdInput,
  ]);

  // Prompt 142B: the advanced filter popover is a real anchored popover
  // (dismissible by outside click or Escape) - same pattern War Room's own
  // "ตัวกรองเพิ่มเติม" popover already established, reused here for the same
  // reason (a real command-console control, not an always-full-width inline
  // form).
  useEffect(() => {
    if (!filterPanelOpen) {
      return undefined;
    }

    function handleOutsideClick(event) {
      if (filterAnchorRef.current && !filterAnchorRef.current.contains(event.target)) {
        setFilterPanelOpen(false);
      }
    }

    function handleKeyDown(event) {
      if (event.key === "Escape") {
        setFilterPanelOpen(false);
      }
    }

    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [filterPanelOpen]);

  const filters = useMemo(
    () => ({
      page,
      pageSize: ABNORMAL_EVENTS_DEFAULT_PAGE_SIZE,
      search,
      // excludeMissingHeartbeat never becomes a real query param - the
      // backend can't express "not this type" in one request (see
      // STATUS_TABS' own doc comment) - it's applied client-side below.
      abnormalType: excludeMissingHeartbeat ? "" : abnormalTypeFilter,
      severity: severityFilter,
      status: statusFilter,
      superAgentOrganizationId: superAgentOrgInput,
      plcOrganizationId: plcOrgInput,
      agentId: agentIdInput,
      employerId: employerIdInput,
      workerId: workerIdInput,
    }),
    [
      page,
      search,
      abnormalTypeFilter,
      severityFilter,
      statusFilter,
      excludeMissingHeartbeat,
      superAgentOrgInput,
      plcOrgInput,
      agentIdInput,
      employerIdInput,
      workerIdInput,
    ]
  );

  const { status: listStatus, data: listData, error: listError, refetch: refetchList } = useAbnormalEventsList(filters);

  /**
   * Deliberately NOT `filters`: the tiles must not move when a tab is clicked,
   * so status/type are left out. Everything an operator narrowed by hand - the
   * search box and the More filters popover - is kept, because a strip that
   * ignored those would be describing a different list from the one on screen.
   */
  const summaryFilters = useMemo(
    () => ({
      search,
      severity: severityFilter,
      superAgentOrganizationId: superAgentOrgInput,
      plcOrganizationId: plcOrgInput,
      agentId: agentIdInput,
      employerId: employerIdInput,
      workerId: workerIdInput,
    }),
    [search, severityFilter, superAgentOrgInput, plcOrgInput, agentIdInput, employerIdInput, workerIdInput]
  );
  const summary = useAbnormalSummaryCounts(summaryFilters, summaryReloadToken);

  /**
   * Export: the SAME filtered list, paged out of the server and written as a
   * real CSV. There is no export endpoint; this does not pretend there is one,
   * and it says so out loud when its own row cap truncates the file.
   */
  function handleExport() {
    setExportState({ status: "working", message: null });
    fetchAllAbnormalEvents(filters)
      .then(({ items: allItems, totalCount: matched, truncated }) => {
        const rows = excludeMissingHeartbeat
          ? allItems.filter((item) => item.abnormalType !== "MissingHeartbeat")
          : allItems;
        const stamp = new Date().toISOString().slice(0, 10);
        const written = downloadCsv(toCsv(rows), `abnormal-events-${stamp}.csv`);
        if (!written) {
          setExportState({ status: "idle", message: "เบราว์เซอร์นี้บันทึกไฟล์ไม่ได้" });
          return;
        }
        setExportState({
          status: "idle",
          message: truncated
            ? `บันทึกแล้ว ${rows.length.toLocaleString("en-US")} รายการ จากทั้งหมด ${matched.toLocaleString("en-US")} รายการ (จำกัดไฟล์ละ ${MAX_ROWS.toLocaleString("en-US")} รายการ กรุณากรองให้แคบลงถ้าต้องการครบ)`
            : `บันทึกแล้ว ${rows.length.toLocaleString("en-US")} รายการ`,
        });
      })
      .catch(() => {
        setExportState({ status: "idle", message: "ดึงข้อมูลเพื่อ export ไม่สำเร็จ กรุณาลองใหม่อีกครั้ง" });
      });
  }

  useEffect(() => {
    if (!selectedEventId) {
      setDetailState({ status: "idle", data: null, error: null, forId: null });
      return undefined;
    }

    let cancelled = false;
    setDetailState((previous) => ({
      status: "loading",
      data: previous.forId === selectedEventId ? previous.data : null,
      error: null,
      forId: selectedEventId,
    }));

    getAbnormalEventDetail(selectedEventId)
      .then((data) => {
        if (!cancelled) {
          setDetailState({ status: "ready", data, error: null, forId: selectedEventId });
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setDetailState({ status: "error", data: null, error, forId: selectedEventId });
        }
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- detailReloadToken intentionally re-triggers a fetch for the same selectedEventId
  }, [selectedEventId, detailReloadToken]);

  function openDetail(eventId, mode = "detail") {
    setSelectedEventId(eventId);
    setDrawerMode(mode);
    setNoteInput("");
    setAcknowledgeState({ status: "idle", error: null });
    setResolutionNoteInput("");
    setResolveValidationError(null);
    setResolveState({ status: "idle", error: null });
  }

  function openScanDrawer() {
    setDrawerMode("scan");
  }

  function closeDrawer() {
    setDrawerMode(null);
    setSelectedEventId(null);
  }

  function handleRefresh() {
    refetchList();
    setSummaryReloadToken((token) => token + 1);
    if (selectedEventId) {
      setDetailReloadToken((token) => token + 1);
    }
  }

  function handleAcknowledgeSubmit() {
    setAcknowledgeState({ status: "submitting", error: null });
    acknowledgeAbnormalEvent(selectedEventId, { note: noteInput })
      .then(() => {
        setAcknowledgeState({ status: "success", error: null });
        setDetailReloadToken((token) => token + 1);
        refetchList();
        setDrawerMode("detail");
      })
      .catch((error) => {
        setAcknowledgeState({ status: "error", error });
      });
  }

  function handleResolveSubmit() {
    if (!resolutionNoteInput.trim()) {
      setResolveValidationError("กรุณาระบุ resolutionNote ก่อนบันทึก");
      return;
    }
    setResolveValidationError(null);
    setResolveState({ status: "submitting", error: null });
    resolveAbnormalEvent(selectedEventId, { resolutionNote: resolutionNoteInput })
      .then(() => {
        setResolveState({ status: "success", error: null });
        setDetailReloadToken((token) => token + 1);
        refetchList();
        setDrawerMode("detail");
      })
      .catch((error) => {
        setResolveState({ status: "error", error });
      });
  }

  function handleScanSubmit() {
    setScanState((previous) => ({ status: "submitting", error: null, result: previous.result }));
    runHeartbeatScan({ nowUtc: scanNowUtcInput ? new Date(scanNowUtcInput).toISOString() : undefined, dryRun: scanDryRun })
      .then((result) => {
        setScanState({ status: "success", error: null, result });
        // A dryRun result never fakes created events - only a real
        // createdCount > 0 (which a dryRun response never has, per the
        // backend's own contract) is worth refetching the list for.
        if (result?.createdCount > 0) {
          refetchList();
        }
      })
      .catch((error) => {
        setScanState((previous) => ({ status: "error", error, result: previous.result }));
      });
  }

  function applyTab(tab) {
    setStatusFilter(tab.status ?? "");
    setAbnormalTypeFilter(tab.abnormalType ?? "");
    setExcludeMissingHeartbeat(Boolean(tab.excludeMissingHeartbeat));
  }

  const activeTabKey = useMemo(() => {
    const match = STATUS_TABS.find((tab) => {
      if (tab.key === "all") {
        return !statusFilter && !abnormalTypeFilter && !excludeMissingHeartbeat;
      }
      return (
        (tab.status ?? "") === statusFilter &&
        (tab.abnormalType ?? "") === abnormalTypeFilter &&
        Boolean(tab.excludeMissingHeartbeat) === excludeMissingHeartbeat
      );
    });
    return match?.key ?? null;
  }, [statusFilter, abnormalTypeFilter, excludeMissingHeartbeat]);

  function clearAllFilters() {
    setSearchInput("");
    setSearch("");
    setAbnormalTypeFilter("");
    setSeverityFilter("");
    setStatusFilter("");
    setExcludeMissingHeartbeat(false);
    setSuperAgentOrgInput("");
    setPlcOrgInput("");
    setAgentIdInput("");
    setEmployerIdInput("");
    setWorkerIdInput("");
  }

  const rawItems = listData?.items ?? [];
  // See STATUS_TABS' own doc comment - the only client-side post-filter on
  // this page, and only ever applied when the "Other Abnormal" tab is
  // active (the backend genuinely cannot express this exclusion in one
  // request).
  const items = excludeMissingHeartbeat ? rawItems.filter((item) => item.abnormalType !== "MissingHeartbeat") : rawItems;
  const totalCount = listData?.totalCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / ABNORMAL_EVENTS_DEFAULT_PAGE_SIZE));
  const hasActiveFilters = Boolean(
    search ||
      abnormalTypeFilter ||
      severityFilter ||
      statusFilter ||
      excludeMissingHeartbeat ||
      superAgentOrgInput ||
      plcOrgInput ||
      agentIdInput ||
      employerIdInput ||
      workerIdInput
  );
  const advancedFilterCount = [superAgentOrgInput, plcOrgInput, agentIdInput, employerIdInput, workerIdInput].filter(Boolean).length;

  const isInitialLoading = listStatus === "loading" && listData === null;
  const isInitialError = listStatus === "error" && listData === null;

  if (isInitialLoading) {
    return (
      <AppShell crumbs={CRUMBS}>
        <PageSkeleton variant="table" label="กำลังโหลดรายการเหตุผิดปกติ..." />
    </AppShell>
    );
  }

  if (isInitialError) {
    return (
      <AppShell crumbs={CRUMBS}>
        <div className="wx-state-card wx-state-card--danger" role="alert">
          <h1 className="wx-state-title">ไม่สามารถโหลดรายการเหตุผิดปกติได้</h1>
          <p className="wx-state-body">{describeListError(listError)}</p>
          <button type="button" className="wx-button wx-button--primary" onClick={handleRefresh}>
            ลองใหม่อีกครั้ง
          </button>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell crumbs={CRUMBS}>
      <div className="wx-abn-page">
        <header className="wx-abn-head">
          {/* The mark and the title are ONE group. As separate children of a
              space-between row they were spread apart and the title drifted to
              the middle of the page. */}
          <div className="wx-abn-head-id">
          {/* P166: the screen's own mark, matching the other console pages. */}
          <span className="wx-abn-head-icon" aria-hidden="true">
            <Activity size={22} />
          </span>
          <div className="wx-abn-title">
            <h1>{PAGE_TITLE}</h1>
            <p>{SUBTITLE_TEXT}</p>
            <p className="wx-abn-helper-text">{HELPER_TEXT}</p>
          </div>
          </div>
          <div className="wx-abn-head-actions">
            {primaryRole && (
              <span className="wx-abn-scope-pill">
                Scope: <b>{primaryRole}</b>
              </span>
            )}
            <button type="button" className="wx-button wx-button--ghost wx-abn-refresh-button" onClick={handleRefresh}>
              <RefreshCw size={14} aria-hidden="true" /> Refresh
            </button>
            {primaryRole === "SuperAdmin" && (
              <button type="button" className="wx-button wx-button--ghost wx-abn-admin-tools-button" onClick={openScanDrawer}>
                <Settings size={14} aria-hidden="true" /> Admin tools
              </button>
            )}
          </div>
        </header>

        {/* P166: six REAL server counts. Not the tiles Prompt 142B deleted -
            those said "รอ Summary API" and stood in for an aggregate endpoint
            that still does not exist. These ask the events list itself for its
            own totalCount, which is a real answer to a real question. See
            useAbnormalSummaryCounts for the one subtraction and why it is
            exact. */}
        <div className="wx-abn-stats">
          {SUMMARY_TILES.map((tile) => (
            <div className={`wx-abn-stat wx-abn-stat--${tile.tone}`} key={tile.key}>
              <span className="wx-abn-stat-icon" aria-hidden="true">
                <tile.Icon size={20} />
              </span>
              <div className="wx-abn-stat-text">
                <span className="wx-abn-stat-label">{tile.label}</span>
                <strong className="wx-abn-stat-value">
                  {summary.counts ? summary.counts[tile.key].toLocaleString("en-US") : "-"}
                </strong>
                <span className="wx-abn-stat-unit">รายการ</span>
              </div>
            </div>
          ))}
        </div>

        <div className="wx-abn-tabs" role="tablist" aria-label="มุมมองเหตุผิดปกติ">
          {STATUS_TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={activeTabKey === tab.key}
              className={`wx-abn-tab${activeTabKey === tab.key ? " wx-abn-tab--active" : ""}`}
              onClick={() => applyTab(tab)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="wx-abn-toolbar">
          <label className="wx-abn-search">
            <span className="wx-visually-hidden">ค้นหารหัส Abnormal, รหัสแรงงาน หรือชื่อแรงงาน</span>
            <input
              type="search"
              placeholder="ค้นหารหัส Abnormal, รหัสแรงงาน หรือชื่อแรงงาน"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
            />
          </label>

          <select
            className="wx-abn-toolbar-select"
            aria-label="Status"
            value={statusFilter}
            onChange={(event) => {
              setStatusFilter(event.target.value);
              setExcludeMissingHeartbeat(false);
            }}
          >
            <option value="">ทุกสถานะ</option>
            {STATUS_OPTIONS.map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>

          <select
            className="wx-abn-toolbar-select"
            aria-label="Severity"
            value={severityFilter}
            onChange={(event) => setSeverityFilter(event.target.value)}
          >
            <option value="">ทุก Severity</option>
            {SEVERITY_OPTIONS.map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>

          <div className="wx-abn-filter-anchor" ref={filterAnchorRef}>
            <button
              type="button"
              className={`wx-button wx-button--ghost wx-abn-more-filters-button${advancedFilterCount > 0 ? " wx-abn-more-filters-button--active" : ""}`}
              aria-expanded={filterPanelOpen}
              onClick={() => setFilterPanelOpen((open) => !open)}
            >
              <Filter size={14} aria-hidden="true" /> More filters
              {advancedFilterCount > 0 && (
                <span className="wx-abn-filter-badge" aria-hidden="true">
                  {advancedFilterCount}
                </span>
              )}
            </button>

            {filterPanelOpen && (
              <div className="wx-abn-filter-panel" role="dialog" aria-label="More filters">
                <div className="wx-abn-filter-section">
                  <h3>Type</h3>
                  <select aria-label="Type" value={abnormalTypeFilter} onChange={(event) => { setAbnormalTypeFilter(event.target.value); setExcludeMissingHeartbeat(false); }}>
                    <option value="">ทั้งหมด</option>
                    {ABNORMAL_TYPE_OPTIONS.map((value) => (
                      <option key={value} value={value}>
                        {value}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="wx-abn-filter-section">
                  <h3>สายงาน</h3>
                  <label className="wx-abn-filter-field">
                    <span>Super Agent Org</span>
                    <input type="text" placeholder="รหัส" value={superAgentOrgInput} onChange={(event) => setSuperAgentOrgInput(event.target.value)} />
                  </label>
                  <label className="wx-abn-filter-field">
                    <span>PLC / บมจ</span>
                    <input type="text" placeholder="รหัส" value={plcOrgInput} onChange={(event) => setPlcOrgInput(event.target.value)} />
                  </label>
                  <label className="wx-abn-filter-field">
                    <span>Agent</span>
                    <input type="text" placeholder="รหัส" value={agentIdInput} onChange={(event) => setAgentIdInput(event.target.value)} />
                  </label>
                </div>

                <div className="wx-abn-filter-section">
                  <h3>งาน</h3>
                  <label className="wx-abn-filter-field">
                    <span>นายจ้าง</span>
                    <input type="text" placeholder="รหัส" value={employerIdInput} onChange={(event) => setEmployerIdInput(event.target.value)} />
                  </label>
                  <label className="wx-abn-filter-field">
                    <span>แรงงาน</span>
                    <input type="text" placeholder="รหัส" value={workerIdInput} onChange={(event) => setWorkerIdInput(event.target.value)} />
                  </label>
                </div>

                {hasActiveFilters && (
                  <button type="button" className="wx-abn-clear-filters-button" onClick={clearAllFilters}>
                    Clear filters
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        <section className="wx-panel wx-abn-list-panel">
          <div className="wx-abn-list-head">
            <h2 className="wx-panel-title">รายการเหตุผิดปกติ</h2>
            <div className="wx-abn-list-head-aside">
            <span className="wx-abn-list-count">
              {excludeMissingHeartbeat ? `${items.length} รายการ (หน้านี้)` : `${totalCount} รายการ`}
              {listStatus === "loading" && listData !== null ? " · กำลังโหลด..." : ""}
            </span>
            {/* Builds a real file from the SAME filtered list, paged out of the
                server - there is no export endpoint and this does not pretend
                otherwise. See exportAbnormalEvents.js. */}
            <button
              type="button"
              className="wx-button wx-button--ghost wx-abn-export-button"
              disabled={exportState.status === "working" || items.length === 0}
              onClick={handleExport}
            >
              <Download size={14} aria-hidden="true" />
              {exportState.status === "working" ? "กำลังเตรียมไฟล์..." : "Export"}
            </button>
            </div>
          </div>

          {exportState.message && (
            <p className="wx-abn-export-message" role="status">
              {exportState.message}
            </p>
          )}

          {listStatus === "error" && listData !== null && (
            <p className="wx-abn-inline-error" role="alert">
              {describeListError(listError)}
            </p>
          )}

          {items.length === 0 ? (
            <div className="wx-abn-empty">
              {hasActiveFilters ? (
                <p>{EMPTY_FILTERED_TEXT}</p>
              ) : (
                <>
                  <p className="wx-abn-empty-title">{EMPTY_NO_EVENTS_TITLE}</p>
                  <p>{EMPTY_NO_EVENTS_SUB}</p>
                  <div className="wx-abn-empty-helper">
                    <strong>{HEARTBEAT_PREREQUISITE_TITLE}</strong>
                    <ul>
                      {HEARTBEAT_PREREQUISITE_STEPS.map((step) => (
                        <li key={step}>{step}</li>
                      ))}
                    </ul>
                  </div>
                </>
              )}
            </div>
          ) : (
            <>
              <div className="wx-abn-table-wrap">
                <table className="wx-abn-table">
                  <colgroup>
                    {/* TWELVE cols for twelve columns, summing to 100.
                        There were THIRTEEN. table-layout:fixed reads them in
                        order and ignores the extra, so the twelve real columns
                        only ever shared 90% and the last 10% of the table was a
                        dead strip - the blank band to the right of ACTION the
                        owner reported. It is invisible in every unit test and in
                        any screenshot narrow enough for the table to fill.
                        If a column is ever added or removed, change this list
                        too: AbnormalHeartbeatPage.test.jsx asserts the two
                        counts match. */}
                    <col style={{ width: "10%" }} /><col style={{ width: "10%" }} /><col style={{ width: "6%" }} /><col style={{ width: "6%" }} />
                    <col style={{ width: "10%" }} /><col style={{ width: "9%" }} /><col style={{ width: "9%" }} /><col style={{ width: "8%" }} />
                    <col style={{ width: "8%" }} /><col style={{ width: "8%" }} /><col style={{ width: "6%" }} /><col style={{ width: "10%" }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>Abnormal Code</th>
                      <th>Type</th>
                      <th>Severity</th>
                      <th>Status</th>
                      <th>แรงงาน</th>
                      <th>Assignment</th>
                      <th>นายจ้าง</th>
                      <th>Agent</th>
                      <th>Detected At (UTC)</th>
                      <th>Last Heartbeat (UTC)</th>
                      <th>Missing (min)</th>
                      <th aria-hidden="true">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item) => {
                      return (
                        <tr key={item.abnormalEventId} className="wx-abn-row" onClick={() => setWorkerRecordId(item.workerId)}>
                          <td>
                            <span className="wx-abn-code">{item.abnormalCode ?? "-"}</span>
                          </td>
                          <td>
                            <span className="wx-badge wx-badge--info">{item.abnormalType}</span>
                          </td>
                          <td>
                            <span className={`wx-badge wx-badge--${severityBadgeVariant(item.severity)}`}>{item.severity}</span>
                          </td>
                          <td>
                            <span className={`wx-badge wx-badge--${statusBadgeVariant(item.status)}`}>{item.status}</span>
                          </td>
                          <td>
                            <span className="wx-abn-cell-strong">{item.workerName ?? "-"}</span>
                            <span className="wx-abn-cell-sub">{item.workerCode ?? "-"}</span>
                          </td>
                          <td>
                            <span className="wx-abn-code">{item.assignmentCode ?? "-"}</span>
                          </td>
                          <td>
                            <span className="wx-abn-cell-strong">{item.employerName ?? "-"}</span>
                          </td>
                          <td>
                            <span className="wx-abn-cell-strong">{item.agentName ?? "-"}</span>
                            <span className="wx-abn-cell-sub">{item.agentCode ?? "-"}</span>
                          </td>
                          <td>
                            <span className="wx-abn-cell-strong">{formatDateTimeTh(item.detectedAtUtc) ?? "-"}</span>
                          </td>
                          <td>
                            <span className="wx-abn-cell-strong">{formatDateTimeTh(item.lastHeartbeatAtUtc) ?? "ยังไม่เคยส่ง"}</span>
                          </td>
                          <td>
                            <strong className={item.minutesSinceLastHeartbeat != null ? "wx-abn-overdue" : undefined}>
                              {item.minutesSinceLastHeartbeat ?? "-"}
                            </strong>
                          </td>
                          <td>
                            {/* P166-R1, owner: "ตัดปุ่ม acknowledge ออก ... และปุ่ม
                                รายละเอียดให้เปลี่ยนเป็น take action". ONE control
                                per row now.

                                Acknowledging is not lost - it never happened here
                                anyway. This button always opened the drawer, and
                                the drawer is where Acknowledge and Resolve live
                                with their own confirmation. Two buttons that both
                                opened the same drawer were the redundancy; the
                                second one just guessed which sub-view you wanted
                                and made every row two controls wide. */}
                            <button
                              type="button"
                              className="wx-abn-row-action"
                              onClick={(event) => {
                                event.stopPropagation();
                                openDetail(item.abnormalEventId, "detail");
                              }}
                            >
                              Take action
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="wx-abn-cards">
                {items.map((item) => (
                  <AbnormalEventCard key={item.abnormalEventId} item={item} onOpen={(mode) => openDetail(item.abnormalEventId, mode)} />
                ))}
              </div>

              <Pagination
                page={page}
                totalPages={totalPages}
                onPageChange={setPage}
              />
            </>
          )}
        </section>

        <footer className="wx-abn-footer">
          <span className="wx-abn-about" tabIndex={0} title={POLICY_TOOLTIP}>
            เกี่ยวกับข้อมูลนี้
          </span>
        </footer>
      </div>

      <DetailDrawer
        open={drawerMode !== null}
        onClose={closeDrawer}
        labelledBy={DRAWER_TITLE_ID}
        selectableSelector="tr.wx-abn-row, .wx-abn-card, .wx-abn-row-action, .wx-abn-refresh-button, .wx-abn-admin-tools-button"
      >
        {drawerMode === "scan" && (
          <ScanDrawerView
            titleId={DRAWER_TITLE_ID}
            nowUtcInput={scanNowUtcInput}
            onNowUtcChange={setScanNowUtcInput}
            dryRun={scanDryRun}
            onDryRunChange={setScanDryRun}
            scanState={scanState}
            onSubmit={handleScanSubmit}
          />
        )}
        {drawerMode === "detail" && (
          <EventDetailView
            titleId={DRAWER_TITLE_ID}
            state={detailState}
            onAcknowledge={() => setDrawerMode("acknowledge")}
            onResolve={() => setDrawerMode("resolve")}
          />
        )}
        {drawerMode === "acknowledge" && (
          <AcknowledgeDrawerView
            titleId={DRAWER_TITLE_ID}
            detail={detailState.data}
            note={noteInput}
            onNoteChange={setNoteInput}
            submitState={acknowledgeState}
            onSubmit={handleAcknowledgeSubmit}
            onBack={() => setDrawerMode("detail")}
          />
        )}
        {drawerMode === "resolve" && (
          <ResolveDrawerView
            titleId={DRAWER_TITLE_ID}
            detail={detailState.data}
            resolutionNote={resolutionNoteInput}
            onResolutionNoteChange={setResolutionNoteInput}
            validationError={resolveValidationError}
            submitState={resolveState}
            onSubmit={handleResolveSubmit}
            onBack={() => setDrawerMode("detail")}
          />
        )}
      </DetailDrawer>
      
        <WorkerRecordDrawer
        open={workerRecordId !== null}
        workerId={workerRecordId}
        onClose={() => setWorkerRecordId(null)}
        selectableSelector="tr.wx-abn-row, .wx-abn-card"
      />
    </AppShell>
  );
}

function AbnormalEventCard({ item, onOpen }) {
  return (
    <div className="wx-abn-card" onClick={() => onOpen("detail")}>
      <div className="wx-abn-card-top">
        <div>
          <strong>{item.abnormalCode ?? "-"}</strong>
          <span className="wx-abn-card-sub">
            {item.workerName ?? "-"} · {item.workerCode ?? "-"}
          </span>
        </div>
        <div className="wx-abn-card-tags">
          <span className={`wx-badge wx-badge--${severityBadgeVariant(item.severity)}`}>{item.severity}</span>
          <span className={`wx-badge wx-badge--${statusBadgeVariant(item.status)}`}>{item.status}</span>
        </div>
      </div>
      <div className="wx-abn-card-grid">
        <div>
          <label>Last Heartbeat</label>
          <strong>{formatDateTimeTh(item.lastHeartbeatAtUtc) ?? "ยังไม่เคยส่ง"}</strong>
        </div>
        <div>
          <label>ขาดหายไป (นาที)</label>
          <strong className={item.minutesSinceLastHeartbeat != null ? "wx-abn-overdue" : undefined}>{item.minutesSinceLastHeartbeat ?? "-"}</strong>
        </div>
        <div>
          <label>Agent</label>
          <strong>{item.agentName ?? "-"}</strong>
        </div>
      </div>
      {/* The same single control the table row carries - a card that offered a
          different action from the row it mirrors would be two designs. */}
      <button
        type="button"
        className="wx-abn-row-action wx-abn-row-action--full"
        onClick={(event) => {
          event.stopPropagation();
          onOpen("detail");
        }}
      >
        Take action
      </button>
    </div>
  );
}

function EventDetailView({ titleId, state, onAcknowledge, onResolve }) {
  if (state.status === "loading" || state.status === "idle") {
    return (
      <DetailSkeleton label="กำลังโหลดรายละเอียด..." />
    );
  }

  if (state.status === "error") {
    return (
      <div className="wx-abn-drawer-state" role="alert">
        <h2 id={titleId} className="wx-state-title">
          ไม่สามารถโหลดรายละเอียดได้
        </h2>
        <p className="wx-state-body">{describeDetailError(state.error)}</p>
      </div>
    );
  }

  const detail = state.data;
  const nextActions = detail.nextActions ?? {};
  const recipients = detail.recipients ?? [];

  return (
    <div className="wx-abn-drawer">
      <RecordDrawerHeader
        titleId={titleId}
        avatarText={detail.workerName}
        title={detail.workerName ?? "-"}
        subtitle={detail.workerCode ?? "-"}
        badges={[
          { label: detail.severity, variant: severityBadgeVariant(detail.severity) },
          { label: detail.status, variant: statusBadgeVariant(detail.status) },
          { label: detail.abnormalCode ?? "-", variant: "neutral" },
        ]}
      />

      <section className="wx-abn-dsec">
        <h3>เหตุการณ์</h3>
        <div className="wx-abn-kv-row">
          <span>ประเภท</span>
          <strong>{detail.abnormalType}</strong>
        </div>
        <div className="wx-abn-kv-row">
          <span>ตรวจพบเมื่อ</span>
          <strong>{formatDateTimeTh(detail.detectedAtUtc) ?? "-"}</strong>
        </div>
        <div className="wx-abn-kv-row">
          <span>Heartbeat ล่าสุด (ตอนตรวจพบ)</span>
          <strong>{formatDateTimeTh(detail.lastHeartbeatAtUtc) ?? "-"}</strong>
        </div>
        <div className="wx-abn-kv-row">
          <span>Heartbeat ล่าสุด (ปัจจุบัน)</span>
          <strong>{formatDateTimeTh(detail.currentLastHeartbeatAtUtc) ?? "ยังไม่มีข้อมูล"}</strong>
        </div>
        <div className="wx-abn-kv-row">
          <span>สถานะ Heartbeat ปัจจุบัน</span>
          <strong>
            <span className="wx-badge wx-badge--danger">{detail.currentHeartbeatState ?? "ไม่ทราบ"}</span>
          </strong>
        </div>
        {detail.notes && (
          <div className="wx-abn-kv-row">
            <span>บันทึกเพิ่มเติม</span>
            <strong>{detail.notes}</strong>
          </div>
        )}
      </section>

      <section className="wx-abn-dsec">
        <h3>แรงงาน / Assignment</h3>
        <div className="wx-abn-kv-row">
          <span>Worker</span>
          <strong>
            {detail.workerName ?? "-"} · {detail.workerCode ?? "-"}
          </strong>
        </div>
        <div className="wx-abn-kv-row">
          <span>Assignment</span>
          <strong className="wx-abn-code">{detail.assignmentCode ?? "-"}</strong>
        </div>
        <div className="wx-abn-kv-row">
          <span>นายจ้าง</span>
          <strong>{detail.employerName ?? "-"}</strong>
        </div>
        <div className="wx-abn-kv-row">
          <span>Agent</span>
          <strong>
            {detail.agentName ?? "-"} · {detail.agentCode ?? "-"}
          </strong>
        </div>
      </section>

      <section className="wx-abn-dsec">
        <h3>Recipients ที่เตรียมไว้ ({recipients.length})</h3>
        {recipients.length === 0 ? (
          <p className="wx-abn-note-muted wx-abn-note-muted--left">ยังไม่มี recipient ที่เตรียมไว้สำหรับเหตุการณ์นี้</p>
        ) : (
          <div className="wx-abn-recipient-list">
            {recipients.map((recipient) => (
              <div className="wx-abn-recipient-row" key={recipient.recipientId}>
                <span className={`wx-badge wx-badge--${recipient.recipientType === "Agent" ? "info" : "neutral"}`}>{recipient.recipientType}</span>
                <span className="wx-abn-recipient-name">{recipient.recipientAgentId ?? recipient.recipientWorkerId ?? recipient.recipientUserId ?? "-"}</span>
                <span className="wx-badge wx-badge--neutral">{recipient.deliveryStatus}</span>
              </div>
            ))}
          </div>
        )}
        <p className="wx-abn-note-muted wx-abn-note-muted--left">
          ระบบยังไม่แจ้งให้อัตโนมัติ กรุณาติดต่อแรงงานหรือหัวหน้างานเอง
        </p>
      </section>

      {(nextActions.canAcknowledge || nextActions.canResolve || nextActions.canViewFootstepHistory) && (
        <section className="wx-abn-dsec">
          <h3>ขั้นตอนถัดไป</h3>
          <div className="wx-abn-next-actions">
            {nextActions.canAcknowledge && (
              <button type="button" className="wx-abn-row-action wx-abn-row-action--full" onClick={onAcknowledge}>
                รับทราบ (Acknowledge)
              </button>
            )}
            {nextActions.canResolve && (
              <button type="button" className="wx-abn-row-action wx-abn-row-action--full" onClick={onResolve}>
                ปิดเหตุ (Resolve)
              </button>
            )}
            {nextActions.canViewFootstepHistory && detail.workerId && (
              <Link to="/backoffice/tracking/footsteps" className="wx-abn-row-action wx-abn-row-action--full wx-abn-row-action--ghost">
                ดูประวัติ Footstep (อ้างอิง)
              </Link>
            )}
          </div>
        </section>
      )}
    </div>
  );
}

function ScanDrawerView({ titleId, nowUtcInput, onNowUtcChange, dryRun, onDryRunChange, scanState, onSubmit }) {
  const submitting = scanState.status === "submitting";
  const result = scanState.result;

  return (
    <div className="wx-abn-drawer">
      <span className="wx-abn-drawer-kicker">ADMIN TOOLS</span>
      <h2 id={titleId} className="wx-abn-drawer-title">
        Diagnostic: ตรวจหา Missing Heartbeat
      </h2>
      <p className="wx-abn-drawer-sub">ตรวจหาแรงงานที่ heartbeat หายเกิน threshold</p>

      <div className="wx-abn-notice wx-abn-notice--warn">
        {SCAN_WARNING_LINES.map((line) => (
          <span key={line} className="wx-abn-notice-line">
            {line}
          </span>
        ))}
      </div>

      <section className="wx-abn-dsec">
        <h3>พารามิเตอร์</h3>
        <label className="wx-abn-field">
          <span>
            nowUtc <span className="wx-abn-optional">(ไม่บังคับ)</span>
          </span>
          <input
            type="datetime-local"
            aria-label="nowUtc"
            value={nowUtcInput}
            onChange={(event) => onNowUtcChange(event.target.value)}
            placeholder="ค่าเริ่มต้น: เวลาปัจจุบัน"
          />
        </label>
        <label className="wx-abn-field wx-abn-toggle-field">
          <span>โหมดทดลอง</span>
          <span className="wx-abn-toggle">
            <input type="checkbox" aria-label="โหมดทดลอง" checked={dryRun} onChange={(event) => onDryRunChange(event.target.checked)} />
            <span className="wx-abn-toggle-track">
              <span className="wx-abn-toggle-thumb" />
            </span>
          </span>
        </label>
        <span className="wx-abn-field-hint">เมื่อเปิด dryRun ระบบจะคืนค่าผู้สมัคร (candidates) โดยไม่บันทึกข้อมูลใด ๆ</span>
        <button type="button" className="wx-button wx-button--primary wx-abn-run-scan-button" disabled={submitting} onClick={onSubmit}>
          {submitting ? "กำลังสแกน..." : "เริ่มการสแกน"}
        </button>
      </section>

      {scanState.status === "error" && (
        <p className="wx-abn-form-message" role="alert">
          {describeScanError(scanState.error)}
        </p>
      )}

      {result && (
        <section className="wx-abn-dsec">
          <h3>ผลลัพธ์</h3>
          <div className="wx-abn-kv-row">
            <span>โหมดทดลอง</span>
            <strong>{String(result.dryRun)}</strong>
          </div>
          {SCAN_RESULT_FIELD_LABELS.map(([field, label]) => (
            <div className="wx-abn-kv-row" key={field}>
              <span>{label}</span>
              <strong>{result[field]}</strong>
            </div>
          ))}
          <p className="wx-abn-note-muted wx-abn-note-muted--left">
            ระบบยังไม่แจ้งให้อัตโนมัติ กรุณาติดต่อแรงงานหรือหัวหน้างานเอง
          </p>
        </section>
      )}
    </div>
  );
}

function AcknowledgeDrawerView({ titleId, detail, note, onNoteChange, submitState, onSubmit, onBack }) {
  const submitting = submitState.status === "submitting";

  return (
    <div className="wx-abn-drawer">
      <span className="wx-abn-drawer-kicker">ACKNOWLEDGE</span>
      <h2 id={titleId} className="wx-abn-drawer-title">
        รับทราบเหตุผิดปกติ
      </h2>
      <p className="wx-abn-drawer-sub">
        {detail?.abnormalCode ?? "-"} · {detail?.workerName ?? "-"}
      </p>
      <div className="wx-abn-drawer-status">
        <span className="wx-badge wx-badge--warning">Open -&gt; Acknowledged</span>
      </div>

      <section className="wx-abn-dsec">
        <h3>รายละเอียด</h3>
        <label className="wx-abn-field">
          <span>
            note <span className="wx-abn-optional">(ไม่บังคับ)</span>
          </span>
          <textarea
            aria-label="note"
            value={note}
            onChange={(event) => onNoteChange(event.target.value)}
            placeholder="เช่น ติดต่อ Agent แล้ว รอผลตรวจสอบเพิ่มเติม"
          />
        </label>
      </section>

      <div className="wx-abn-notice">
        {ACKNOWLEDGE_NOTICE_LINES.map((line) => (
          <span key={line} className="wx-abn-notice-line">
            {line}
          </span>
        ))}
      </div>

      {submitState.status === "error" && (
        <p className="wx-abn-form-message" role="alert">
          {describeWriteError(submitState.error, "เหตุผิดปกตินี้ถูกปิดหรือรับทราบแล้ว ไม่สามารถ acknowledge ซ้ำได้")}
        </p>
      )}

      <div className="wx-abn-drawer-actions">
        <button type="button" className="wx-button wx-button--ghost" onClick={onBack} disabled={submitting}>
          ย้อนกลับ
        </button>
        <button type="button" className="wx-button wx-button--primary" onClick={onSubmit} disabled={submitting}>
          {submitting ? "กำลังบันทึก..." : "ยืนยัน Acknowledge"}
        </button>
      </div>
    </div>
  );
}

function ResolveDrawerView({ titleId, detail, resolutionNote, onResolutionNoteChange, validationError, submitState, onSubmit, onBack }) {
  const submitting = submitState.status === "submitting";

  return (
    <div className="wx-abn-drawer">
      <span className="wx-abn-drawer-kicker">RESOLVE</span>
      <h2 id={titleId} className="wx-abn-drawer-title">
        ปิดเหตุผิดปกติ (Resolve)
      </h2>
      <p className="wx-abn-drawer-sub">
        {detail?.abnormalCode ?? "-"} · {detail?.workerName ?? "-"}
      </p>
      <div className="wx-abn-drawer-status">
        <span className="wx-badge wx-badge--warning">{detail?.status ?? "Open"} -&gt; Resolved</span>
      </div>

      <section className="wx-abn-dsec">
        <h3>รายละเอียด</h3>
        <label className="wx-abn-field">
          <span>
            หมายเหตุการปิดเรื่อง <b>*</b>
          </span>
          <textarea
            aria-label="หมายเหตุการปิดเรื่อง"
            value={resolutionNote}
            onChange={(event) => onResolutionNoteChange(event.target.value)}
            placeholder="เช่น ติดต่อแรงงานทางโทรศัพท์แล้ว ยืนยันปลอดภัย อุปกรณ์แบตเตอรี่หมด"
          />
        </label>
      </section>

      {validationError && (
        <p className="wx-abn-form-message" role="alert">
          {validationError}
        </p>
      )}

      <div className="wx-abn-notice wx-abn-notice--warn">{RESOLVE_WARNING_TEXT}</div>

      {submitState.status === "error" && (
        <p className="wx-abn-form-message" role="alert">
          {describeWriteError(submitState.error, "เหตุผิดปกตินี้ถูกปิดไปแล้ว ไม่สามารถ resolve ซ้ำได้")}
        </p>
      )}

      <div className="wx-abn-drawer-actions">
        <button type="button" className="wx-button wx-button--ghost" onClick={onBack} disabled={submitting}>
          ย้อนกลับ
        </button>
        <button type="button" className="wx-button wx-button--primary" onClick={onSubmit} disabled={submitting}>
          {submitting ? "กำลังบันทึก..." : "ยืนยัน Resolve"}
        </button>
      </div>
    </div>
  );
}
